import type { LWApiPublicAssetReportRelatedTerminationActionsResultRaw } from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import Imunify360Icon from '@liquidweb-dev/undercurrent/icons/platform/Imunify360';
import ThreatDownIcon from '@liquidweb-dev/undercurrent/icons/platform/ThreatDown';
import ShieldCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/ShieldCheck';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import ListChecksIcon from '@liquidweb-dev/undercurrent/icons/lucide/ListChecks';
import CctvIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cctv';
import dayjs from 'dayjs';
import useBackups from 'components/TotalBackupsBlock/useBackups';
import CircleFadingPlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleFadingPlus';

interface AddonIconWithTextProps {
	type: string;
	uniqId: string;
	title?: string;
}

const AddonIconWithText = ({ type, uniqId, title }: AddonIconWithTextProps) => {
	const { totalSize } = useBackups(uniqId);
	const backupUsed = totalSize || '0 GB';
	switch (type) {
		case 'Acronis':
		case 'AcronisBackup':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<AcronisIcon
						sx={({ size }) => ({
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Acronis backups
					</Typography>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						{backupUsed}
					</Typography>
				</Stack>
			);
		case 'ServerSecurity.Imunify360':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<Imunify360Icon
						sx={({ size }) => ({
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Imunify360
					</Typography>
				</Stack>
			);
		case 'ServerSecurity.ThreatDown':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<ThreatDownIcon
						sx={({ size }) => ({
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Threatdown
					</Typography>
				</Stack>
			);
		case 'DDoS.Server.Protect':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<ShieldCheckIcon
						sx={({ palette, size }) => ({
							color: palette.uc.highlight.icon.main,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Advanced DDoS Protection
					</Typography>
				</Stack>
			);
		case 'Cloud Backup':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<CloudIcon
						sx={({ palette, size }) => ({
							color: palette.uc.highlight.icon.main,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Cloud backups
					</Typography>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
					>
						{backupUsed}
					</Typography>
				</Stack>
			);
		case 'Vulnerability.Assess':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<ListChecksIcon
						sx={({ palette, size }) => ({
							color: palette.uc.highlight.icon.main,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Vulnerability Assessment
					</Typography>
				</Stack>
			);
		case 'ServerSecurePlus':
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<CctvIcon
						sx={({ palette, size }) => ({
							color: palette.uc.highlight.icon.main,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						Server Secure Plus
					</Typography>
				</Stack>
			);
		default:
			return (
				<Stack direction="row" sx={{ gap: 100 }}>
					<CircleFadingPlusIcon
						sx={({ palette, size }) => ({
							color: palette.uc.highlight.icon.main,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<Typography
						fontType="monospace"
						variant="label-md"
						sx={{ fontWeight: 500 }}
					>
						{title || type}
					</Typography>
				</Stack>
			);
	}
};

interface AddonProps {
	data: LWApiPublicAssetReportRelatedTerminationActionsResultRaw;
	cancellationDate: string | undefined;
}

const Addon = ({ data, cancellationDate }: AddonProps) => {
	const { type, title, uniq_id: uniqId } = data;
	return (
		<Stack
			direction="row"
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 300,
				width: '100%',
			}}
		>
			<AddonIconWithText type={type} uniqId={uniqId} title={title} />
			<Typography fontType="monospace" variant="label-md">
				Cancels on {dayjs(cancellationDate).format('LL')}
			</Typography>
		</Stack>
	);
};

export default Addon;
